/**
 * WHICH APP THIS SIGN-IN OPENS (2026-09-25, the Revent mobile first touch).
 *
 * `/?app=revent` sends a signed-out person to
 * `/login?signup=1&returnTo=%2F%3Fapp%3Drevent` (app/(splash)/authed/home/page.tsx),
 * and the screen they landed on said "Sign up for Robutler" and nothing about
 * Revent or the week. The design calls that screen the single highest-leverage
 * one in the funnel (docs/internal/architecture/revent-mobile-first-touch.md,
 * section 5.5 step 3). This module works out, on the client and from nothing a
 * link could forge, which app the screen should name:
 *
 *   1. The `returnTo` the page will come back to, when it is the `/?app=<id>`
 *      funnel. The id is looked up in `FLAGSHIP_APPS`, the same constant list
 *      the funnel itself resolves against, so a link can only ever name an app
 *      the portal already knows the display name of. Any other id, and any
 *      other path, names nothing and the screen stays exactly as it was.
 *   2. A pending ARRIVAL FRAGMENT: a scanned Revent code that arrived on
 *      `location.hash`, or one already parked in sessionStorage by a
 *      client-side hop (the `/w/` share surface's sign-in control, or an app's
 *      `ui.signIn`), which navigates to a bare `/login` with no fragment and
 *      no `?app=`. The fragment grammar is Revent's arrival vocabulary today
 *      (`c` a code, `r` a card reference, `n` a name, `e` an event; see
 *      lib/auth/arrival-fragment.ts), so a fragment that parses IS a Revent
 *      arrival. `ARRIVAL_FRAGMENT_APP_ID` is the one place to revisit when
 *      another app adopts the carriage.
 *
 * THE STASH IS PEEKED, NEVER TAKEN. `takeArrivalFragment` clears the slot on
 * read so that exactly one page redeems a code; the login screen is not that
 * page. `peekArrivalFragment` applies the same record checks (shape, age,
 * grammar) and leaves the slot alone.
 *
 * ONE THING THE FRAGMENT CARRIES IS DRAWN: THE INVITER'S NAME, AND ONLY WHEN
 * IT READS AS A PERSON'S NAME (the owner, 2026-10-03). Until then this module
 * surfaced nothing a link carried, and the screen said "Someone at SF Tech
 * Week showed you their code. It is kept on this device, nothing has been
 * sent, and Revent will pick it up once you sign in." The owner: "too boring
 * and confusing, drop; instead just say that person Firstname Lastname
 * invited you to connect on Revent". The name is still what it always was, a
 * string anybody can put in a link, never an identity, and this is the
 * platform's own sign-in screen, so `inviterNameFromFragment` draws it only
 * under a narrow shape: words of letters and combining marks separated by
 * single spaces, an apostrophe or a hyphen allowed inside a word and a full
 * stop only at its end, at most `INVITER_NAME_MAX` code points, and none of
 * the words a platform or a support desk would sign with. Anything else (a digit, an address, a link,
 * a control or direction character, "Robutler Support") is not drawn and the
 * screen says "You were invited to connect" with no name. The sentence
 * around the name is fixed and the name is drawn as text. The code, the
 * card reference and the event are still never drawn. SECURITY_ISSUES_LOG.md
 * records the change (2026-10-03).
 *
 * WHICH PROJECT OF THE APP (2026-10-04, the owner: "make the login image
 * defined by project"). An app's sign-in screen may name projects, each with
 * its own line and picture (lib/workspaces/flagship-apps.ts): Revent's are
 * conferences. `LoginApp.project` is the one this sign-in is for, and it can
 * only ever be a key of that constant table: the conference a fragment names
 * (`f`, a folder id) when the table knows it; nothing when the fragment names
 * a conference it does not know, or carries a code or an event and no
 * conference (whose it is cannot be told from here); and the app's default
 * project for the bare funnel. So a link chooses among pictures that ship
 * with the page and can never bring one.
 *
 * WHO SENT THE PERSON, AND THE INVITER'S INITIAL (2026-10-04 night, the
 * sign-in screens for the Data Phoenix send). Two more things a link may
 * CHOOSE and never bring. `signInPartnerName` reads the campaign's source
 * tag (`utm_source`, which rides to this page beside the return address,
 * lib/http/campaign-tags.ts) and answers the partner's name only when the
 * tag is an own key of the project's fixed `partners` table; the screen then
 * draws "<Partner> x Robutler", and any other tag draws nothing.
 * `inviterInitial` is the first letter of the name `inviterNameFromFragment`
 * already let through, for the circle that stands for the inviter in the
 * picture a scanned code gets (the owner: "for code show two avatars (those
 * who sent you and you) and a connection line"). An initial and not a photo:
 * the code is one-use and never leaves the browser before sign-in, so the
 * name it carries is all this screen can know about the person.
 *
 * THE EVENT A SENT LINK NAMES (2026-10-05, the owner: "in hero, event name
 * instead of 'Revent'?", then "ok, lets build it"). `LoginApp.sentEvent` is
 * the event id of a sent link, for the one case where the screen may ask the
 * platform for that event's NAME: a link that carries an event and no
 * conference, which is how an event of the built-in week is shared (the week
 * has no folder in a link, public/widgets/revent/views/event.js `shareLink`).
 * The id is a key and nothing else: the screen sends it to
 * /api/auth/sign-in-event, which looks it up in the catalog the platform
 * itself published and answers that catalog's title or nothing
 * (lib/apps/revent-event-names.ts). An event of a conference somebody made
 * has no `sentEvent`: its title would be an organizer's words on this
 * screen, and that waits for a decision. A code is never asked about.
 *
 * Plain module, client-safe: the login body imports it and so do its tests.
 */

import { FLAGSHIP_BY_APP_ID } from '@/lib/workspaces/flagship-apps';
import {
  ARRIVAL_FRAGMENT_MAX_LEN,
  ARRIVAL_FRAGMENT_STORAGE_KEY,
  ARRIVAL_FRAGMENT_TTL_MS,
  parseArrivalFragment,
  type ArrivalFragmentStore,
} from './arrival-fragment';

/** The app whose arrival vocabulary the fragment grammar is. See the header. */
export const ARRIVAL_FRAGMENT_APP_ID = 'revent';

export interface LoginApp {
  /** The `FLAGSHIP_APPS` id, which is also the `/?app=<id>` funnel id. */
  appId: string;
  /** The display name, from `FLAGSHIP_APPS`, never from the link. */
  title: string;
  /**
   * A scanned code (`c`) or card reference (`r`) is waiting for this sign-in
   * to finish. A fragment carrying only a name or an event is not "a code".
   */
  codePending: boolean;
  /**
   * The person was SENT something: the fragment names an event (`e`) or a
   * conference (`f`) and no code. NOT a link that names only a conference the
   * screen has an entry for: that is the conference's own link and draws its
   * own screen (2026-10-11, see `resolveLoginApp`). Revent's share links and the landing's
   * hand-off carry exactly that (round 6, 2026-09-27), so the screen can
   * say "You were sent an event in Revent" instead of the SF Tech Week line,
   * which is the wrong event for everyone an organizer sends a link to.
   * Only THAT a link was sent is known here; what it names is never drawn.
   */
  sentLink: boolean;
  /**
   * The name the scanned code carries, when a code is pending and the name
   * passes `inviterNameFromFragment`; null otherwise. Unverified: a string to
   * put in "<name> invited you to connect", never an identity.
   */
  invitedBy: string | null;
  /**
   * The project of the app this sign-in is for: a key of the app's
   * `signInScreen.projects`, or null for none the screen knows. See the
   * module header.
   */
  project: string | null;
  /**
   * The event a sent link names, when the screen may ask for its name: a
   * sent link with an event and no conference (an event of the built-in
   * week). Null for a code, for a link with a conference on it, and for an
   * `e` that is not an event id. A key for a lookup, never text to draw.
   */
  sentEvent: string | null;
  /**
   * A sent link that names a conference and no event: somebody's own
   * conference with no screen of its own. Its line says a conference was
   * sent, not an event (the visual pass of 2026-10-13: such a link read "You
   * were sent an event in Revent", now the common fallback since organizers
   * can publish a screen and many will not).
   */
  sentConference: boolean;
}

/** The longest inviter name the sign-in screen draws, in code points. Revent clamps its own to 24. */
export const INVITER_NAME_MAX = 40;
/* A person's name: words separated by single spaces. A word is letters and
   combining marks, with an apostrophe (either spelling) or a hyphen allowed
   INSIDE it, and a full stop allowed only at its END (an initial, "Jr.").
   A full stop followed by a letter is how a domain is spelled, and
   "evil.example invited you to connect" must never be drawn. */
const INVITER_NAME_WORD = "[\\p{L}\\p{M}]+(?:['\\u2019-][\\p{L}\\p{M}]+)*\\.?";
const INVITER_NAME_SHAPE = new RegExp(`^${INVITER_NAME_WORD}(?: ${INVITER_NAME_WORD})*$`, 'u');
/* Words a platform, a brand or a support desk signs with. A link must not be
   able to put them in front of "invited you to connect" on this screen. */
const NOT_A_PERSONS_NAME =
  /\b(?:robutler|revent|linkedin|google|support|admin|administrator|security|official|staff|team|moderator|verify|verified|verification|account|billing|helpdesk|service)\b/i;

/**
 * The inviter's name a fragment in the grammar carries (`n`), when it reads
 * as a person's name; null for no name, a name that does not decode, or one
 * outside the shape above. See the module header.
 */
export function inviterNameFromFragment(fragment: string): string | null {
  const pair = fragment.split('&').find((p) => p.startsWith('n='));
  if (!pair) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(pair.slice(2));
  } catch {
    return null;
  }
  const name = decoded.normalize('NFC').replace(/ +/g, ' ').trim();
  if (!name || Array.from(name).length > INVITER_NAME_MAX) return null;
  if (!INVITER_NAME_SHAPE.test(name) || NOT_A_PERSONS_NAME.test(name)) return null;
  return name;
}

/**
 * The first letter of an inviter's name, upper case, for their circle on the
 * sign-in screen. `name` is what `inviterNameFromFragment` answered (letters,
 * marks, spaces, an apostrophe, a hyphen, a full stop), so the first user-
 * perceived character is a letter; it is taken whole where the runtime can
 * tell (a base letter with its marks), else as one code point.
 */
/* The first letter of a word, taken whole: one grapheme, so a letter with
   its mark and a letter outside the first plane come out as one. */
function firstGrapheme(word: string): string {
  let first = Array.from(word)[0] ?? '';
  try {
    const Segmenter = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
    if (Segmenter) {
      for (const part of new Segmenter(undefined, { granularity: 'grapheme' }).segment(word)) {
        first = part.segment;
        break;
      }
    }
  } catch {
    /* no segmenter: the first code point stands */
  }
  return first;
}
/* TWO INITIALS WHERE THE NAME HAS TWO WORDS (the Revent design rework,
   2026-10-18; the PhoneWalkIn wireframe draws "MC" for Maya Chen where the
   screen drew "B" for Ben Okafor): the first letter of the first and of the
   last word, one letter for a one-word name. */
export function inviterInitial(name: string | null | undefined): string | null {
  if (typeof name !== 'string') return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const words = trimmed.split(/\s+/).filter(Boolean);
  const first = firstGrapheme(words[0]);
  const last = words.length > 1 ? firstGrapheme(words[words.length - 1]) : '';
  const out = first + last;
  return out ? out.toLocaleUpperCase() : null;
}

/* A campaign source tag as our own links write it: lower-case kebab or snake. */
const SOURCE_TAG_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * The partner a campaign's source tag names on a project's sign-in screen, or
 * null. `partners` is the project's fixed table (lib/workspaces/flagship-apps.ts)
 * and `source` is whatever `utm_source` the address carried: it is only ever
 * a KEY, looked up as an own property, so `__proto__`, `constructor` and a
 * tag the table does not list all answer null and nothing from the link is
 * drawn. See the module header.
 */
export function signInPartnerName(
  partners: Readonly<Record<string, string>> | null | undefined,
  source: unknown,
): string | null {
  if (!partners || typeof source !== 'string') return null;
  const key = source.trim().toLowerCase();
  if (!SOURCE_TAG_RE.test(key)) return null;
  if (!Object.prototype.hasOwnProperty.call(partners, key)) return null;
  const name = partners[key];
  return typeof name === 'string' && name ? name : null;
}

/* The flagship ids are lower-case kebab; anything else is not looked up. */
const APP_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
/* Same bound `takeArrivalFragment` applies before it parses a record. */
const MAX_RECORD_LEN = ARRIVAL_FRAGMENT_MAX_LEN + 64;
const PROBE_ORIGIN = 'https://login-app.invalid';

/**
 * The `<id>` of a `/?app=<id>` return address, or null for any other path.
 * The caller has already passed `returnTo` through `isSafeReturnPath`; this
 * only reads the funnel's one parameter off the root path.
 */
export function appIdFromReturnTo(returnTo: unknown): string | null {
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/')) return null;
  let url: URL;
  try {
    url = new URL(returnTo, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN || url.pathname !== '/') return null;
  const id = url.searchParams.get('app');
  return id && APP_ID_RE.test(id) ? id : null;
}

/**
 * Read the parked arrival fragment WITHOUT clearing it. The same checks as
 * `takeArrivalFragment` (size, JSON shape, a finite timestamp within the
 * TTL and not from the future, the grammar), so a record this accepts is
 * one that page would redeem. Storage is not trusted for having been
 * written by us: any script on the origin can write the key.
 */
export function peekArrivalFragment(
  storage: ArrivalFragmentStore | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(ARRIVAL_FRAGMENT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw || raw.length > MAX_RECORD_LEN) return null;
  let record: unknown;
  try {
    record = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!record || typeof record !== 'object') return null;
  const { f, at } = record as { f?: unknown; at?: unknown };
  if (typeof at !== 'number' || !Number.isFinite(at)) return null;
  const age = now - at;
  if (age < 0 || age > ARRIVAL_FRAGMENT_TTL_MS) return null;
  return parseArrivalFragment(f);
}

/* A Revent event id, `ev` and twelve hex digits: the shape the lookup itself
   holds (lib/apps/revent-event-names.ts `REVENT_EVENT_ID_RE`; a literal here
   because that module reads a database and this one ships to the browser; a
   test holds the two equal). */
const SENT_EVENT_RE = /^ev[0-9a-f]{12}$/;

/** The event a fragment in the grammar names (`e`), when it reads as an event id; else null. */
export function eventFromFragment(fragment: string): string | null {
  const pair = fragment.split('&').find((p) => p.startsWith('e='));
  const id = pair ? pair.slice(2) : '';
  return SENT_EVENT_RE.test(id) ? id : null;
}

/** Does a fragment in the grammar carry a one-use code or a card reference? */
export function fragmentCarriesCode(fragment: string): boolean {
  return fragment.split('&').some((pair) => pair.startsWith('c=') || pair.startsWith('r='));
}

/**
 * Does a fragment in the grammar name an event or a conference and no code?
 * That is a link somebody SENT (Share, the landing's hand-off), as opposed
 * to a code somebody showed; a code wins, since its own line says more.
 */
export function fragmentCarriesLink(fragment: string): boolean {
  if (fragmentCarriesCode(fragment)) return false;
  return fragment.split('&').some((pair) => pair.startsWith('e=') || pair.startsWith('f='));
}

/** The conference folder a fragment in the grammar names (`f`), or null. */
export function conferenceFromFragment(fragment: string): string | null {
  const pair = fragment.split('&').find((p) => p.startsWith('f='));
  return pair ? pair.slice(2) : null;
}

/**
 * The app this sign-in opens, or null when the screen should stay generic.
 * `returnTo` decides first; a pending fragment names Revent when the return
 * address does not name anything. Only an app in `FLAGSHIP_APPS` is ever
 * returned, so the title can never come from the link.
 *
 * A CONFERENCE WITH A PUBLISHED SCREEN IS KNOWN TOO (2026-10-13, the owner:
 * "go for the conference-supplied sign-in screen"). `published` lists the
 * conference folders the PLATFORM answered a published sign-in screen for
 * (the operator's publisher wrote it into the folder;
 * lib/apps/revent-sign-in-screens.ts), never anything a link carries: a
 * link to one of them is that conference's own link, exactly as a link to a
 * conference of the constant table is, and it may be the sign-in's project.
 * `fragment` is the arrival fragment as the caller already read it (from the
 * hash or the stash), for a caller that resolves again when an answer comes
 * in; when it is given, `hash` and `storage` are not read.
 */
export function resolveLoginApp(input: {
  /** The already-validated return address, or null. */
  returnTo: unknown;
  /** `location.hash`, when the caller has a window. */
  hash?: unknown;
  /** `window.sessionStorage`, when the caller has one; peeked, never taken. */
  storage?: ArrivalFragmentStore | null;
  now?: number;
  /** The arrival fragment already read; outranks `hash` and `storage` when given (null: none). */
  fragment?: string | null;
  /** Conference folders the platform answered a published sign-in screen for. */
  published?: readonly string[] | null;
}): LoginApp | null {
  const fragment =
    input.fragment !== undefined
      ? parseArrivalFragment(input.fragment)
      : (parseArrivalFragment(input.hash) ?? peekArrivalFragment(input.storage, input.now));
  const appId = appIdFromReturnTo(input.returnTo) ?? (fragment ? ARRIVAL_FRAGMENT_APP_ID : null);
  if (!appId) return null;
  const flagship = FLAGSHIP_BY_APP_ID.get(appId);
  if (!flagship) return null;
  const codePending =
    appId === ARRIVAL_FRAGMENT_APP_ID && !!fragment && fragmentCarriesCode(fragment);
  const invitedBy = codePending && fragment ? inviterNameFromFragment(fragment) : null;
  // THE PROJECT: only ever a key of the app's own constant table, or a
  // conference the platform itself answered a published screen for.
  const projects = flagship.signInScreen?.projects;
  const published = appId === ARRIVAL_FRAGMENT_APP_ID && Array.isArray(input.published) ? input.published : null;
  const known = (id: string | null | undefined): string | null =>
    id &&
    ((projects && Object.prototype.hasOwnProperty.call(projects, id)) || (published && published.includes(id)))
      ? id
      : null;
  const conference =
    appId === ARRIVAL_FRAGMENT_APP_ID && fragment ? conferenceFromFragment(fragment) : null;
  // A LINK THAT NAMES ONLY A CONFERENCE THIS SCREEN KNOWS IS THAT CONFERENCE'S
  // OWN LINK, NOT SOMETHING A PERSON WAS SENT (2026-10-11). The owner asked
  // for links that name a conference ("?app=revent&conference=xxx") so that a
  // conference can be linked to like the bare link; the first signed-out pass
  // of `?conference=la-2026` drew LA Tech Week's picture over "Sign in to
  // Revent" and "You were sent an event in Revent. Sign in to see it and ask
  // for a place.", because every link with a conference on it counted as a
  // sent link. It names no event and nobody sent it. So such a link draws the
  // conference's own screen, words included, exactly as the bare link does
  // for the default one. Still a sent link: any link with an event on it, and
  // a link to a conference this screen has no entry for (somebody's own).
  const ownConferenceLink =
    !!fragment && !!known(conference) && !codePending && !fragment.split('&').some((pair) => pair.startsWith('e='));
  const sentLink =
    appId === ARRIVAL_FRAGMENT_APP_ID && !!fragment && fragmentCarriesLink(fragment) && !ownConferenceLink;
  const project = conference
    ? known(conference)
    : codePending || sentLink
      ? null
      : known(flagship.signInScreen?.defaultProject);
  // Asked about only when the link names an event and no conference at all.
  const sentEvent = sentLink && fragment && !conference ? eventFromFragment(fragment) : null;
  const sentConference =
    sentLink && !!conference && !!fragment && !fragment.split('&').some((pair) => pair.startsWith('e='));
  return { appId: flagship.appId, title: flagship.title, codePending, sentLink, invitedBy, project, sentEvent, sentConference };
}
